/**
 * Matching Results Page
 * 
 * エンジニアと案件のマッチング結果表示ページ
 * 主な機能:
 * - マッチング結果の一覧表示
 * - マッチ度でのソートとフィルタリング
 * - エンジニアへの提案送信
 * - マッチング状態の管理
 * 
 * @component
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Chip,
  Avatar,
  Tab,
  Tabs,
  Badge,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Person,
  Work,
  Send,
  LocationOn,
  Code,
  AttachMoney,
  Schedule,
  Business,
  Check,
  Close,
} from '@mui/icons-material';
import { matchingService } from '../services/supabase';
import { MatchingResult } from '../types';

const Matching: React.FC = () => {
  const navigate = useNavigate();
  const [tabValue, setTabValue] = useState(0);
  const [openSendDialog, setOpenSendDialog] = useState(false);
  const [openStatusDialog, setOpenStatusDialog] = useState(false);
  const [selectedMatch, setSelectedMatch] = useState<MatchingResult | null>(null);
  const [message, setMessage] = useState('');
  const [statusNotes, setStatusNotes] = useState('');
  const [proposalAction, setProposalAction] = useState<'confirmed' | 'cancelled' | null>(null);
  const [matches, setMatches] = useState<MatchingResult[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Load matching results on component mount
  useEffect(() => {
    loadMatchingResults();
  }, []);

  const loadMatchingResults = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await matchingService.getMatchingResults();
      if (response.error) {
        setError(response.error);
      } else {
        setMatches(response.data || []);
      }
    } catch (err) {
      setError('マッチング結果の取得に失敗しました。');
    }
    
    setLoading(false);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'warning';
      case 'sent': return 'info';
      case 'accepted': return 'success';
      case 'rejected': return 'error';
      default: return 'default';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'pending': return '未送信';
      case 'sent': return '送信済み';
      case 'accepted': return '承諾';
      case 'rejected': return '辞退';
      default: return '不明';
    }
  };

  const filteredMatches = matches.filter(match => {
    switch (tabValue) {
      case 0: return true; // すべて
      case 1: return match.status === 'pending'; // 未送信
      case 2: return match.status === 'sent'; // 送信済み
      case 3: return match.status === 'accepted' || match.status === 'rejected'; // 完了
      default: return true;
    }
  });

  const getTabCounts = () => {
    return {
      all: matches.length,
      pending: matches.filter(m => m.status === 'pending').length,
      sent: matches.filter(m => m.status === 'sent').length,
      completed: matches.filter(m => m.status === 'accepted' || m.status === 'rejected').length,
    };
  };

  const tabCounts = getTabCounts();

  const handleSendProposal = (match: MatchingResult) => {
    setSelectedMatch(match);
    setMessage(`${match.engineer.name}様

以下の案件についてご提案させていただきます。

【案件名】${match.project.title}
【会社名】${match.project.client_name || '未設定'}
【予算】¥${match.project.budget?.toLocaleString() || '応相談'}/月
【勤務地】${match.project.location || '未設定'}${match.project.remote_work ? '（リモート可）' : ''}
【必要スキル】${match.project.skills.map(s => s.skill_name).join(', ')}

ご興味がございましたら、詳細をお送りいたします。
ご検討のほど、よろしくお願いいたします。`);
    setOpenSendDialog(true);
  };

  const handleSendMessage = async () => {
    if (!selectedMatch) return;

    try {
      const response = await matchingService.sendProposal(
        selectedMatch.engineer.id, 
        selectedMatch.project.id, 
        message
      );
      
      if (response.error) {
        setError(response.error);
      } else {
        // Reload matching results to reflect the new status
        await loadMatchingResults();
        setOpenSendDialog(false);
        setSelectedMatch(null);
        setMessage('');
      }
    } catch (err) {
      setError('提案の送信に失敗しました。');
    }
  };

  const handleUpdateStatus = (match: MatchingResult, action: 'confirmed' | 'cancelled') => {
    setSelectedMatch(match);
    setProposalAction(action);
    setStatusNotes('');
    setOpenStatusDialog(true);
  };

  const handleConfirmStatusUpdate = async () => {
    if (!selectedMatch || !proposalAction) return;

    try {

      const response = await matchingService.updateProposalStatus(
        selectedMatch.engineer.id,
        selectedMatch.project.id,
        proposalAction,
        statusNotes
      );

      if (response.error) {
        setError(response.error);
      } else {
        await loadMatchingResults();
        setOpenStatusDialog(false);
        setSelectedMatch(null);
        setProposalAction(null);
        setStatusNotes('');
      }
    } catch (err) {
      setError('ステータスの更新に失敗しました。');
    }
  };

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 4, fontWeight: 'bold' }}>
        マッチング結果
      </Typography>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Loading */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {!loading && (
        <>
          {/* タブ */}
          <Card sx={{ mb: 3 }}>
            <Tabs 
              value={tabValue} 
              onChange={(e, value) => setTabValue(value)}
              sx={{ borderBottom: 1, borderColor: 'divider' }}
            >
              <Tab 
                label={
                  <Badge badgeContent={tabCounts.all} color="primary">
                    すべて
                  </Badge>
                } 
              />
              <Tab 
                label={
                  <Badge badgeContent={tabCounts.pending} color="warning">
                    未送信
                  </Badge>
                } 
              />
              <Tab 
                label={
                  <Badge badgeContent={tabCounts.sent} color="info">
                    送信済み
                  </Badge>
                } 
              />
              <Tab 
                label={
                  <Badge badgeContent={tabCounts.completed} color="success">
                    完了
                  </Badge>
                } 
              />
            </Tabs>
          </Card>

          {/* マッチング結果一覧 */}
          <Box sx={{ display: 'grid', gap: 1.5 }}>
            {filteredMatches.map((match) => (
              <Card key={match.id} sx={{ overflow: 'visible' }}>
                <CardContent sx={{ py: 1 }}>
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, gap: 2 }}>
                    {/* エンジニア情報 */}
                    <Card 
                      variant="outlined"
                      onClick={() => navigate(`/engineer/${match.engineer.id}`)}
                      sx={{
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        '&:hover': {
                          backgroundColor: '#f5f5f5',
                          transform: 'translateY(-2px)',
                          boxShadow: 2,
                        },
                      }}
                    >
                      <CardContent sx={{ py: 0.75 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'between', alignItems: 'start', mb: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexGrow: 1 }}>
                            <Avatar sx={{ width: 36, height: 36, bgcolor: '#3498db' }}>
                              <Person />
                            </Avatar>
                            <Box>
                              <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                                {match.engineer.name}
                              </Typography>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                                <LocationOn fontSize="small" color="action" />
                                <Typography variant="body2" color="textSecondary">
                                  {match.engineer.location || '未設定'}
                                </Typography>
                              </Box>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <AttachMoney fontSize="small" color="action" />
                                <Typography variant="body2" color="textSecondary">
                                  ¥{match.engineer.hourly_rate?.toLocaleString() || '応相談'}/月
                                </Typography>
                              </Box>
                            </Box>
                          </Box>
                          <Box sx={{ textAlign: 'center' }}>
                            <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#3498db' }}>
                              {match.matchScore}%
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              マッチ度
                            </Typography>
                          </Box>
                        </Box>

                        {/* スキルマッチング */}
                        <Typography variant="body2" sx={{ mb: 0.25, fontWeight: 'bold' }}>
                          <Code fontSize="small" sx={{ mr: 0.5 }} />
                          スキルマッチング
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.25, mb: 1 }}>
                          {match.skillMatches.map((skillMatch, index) => (
                            <Chip
                              key={`${skillMatch.skill}-${index}`}
                              label={`${skillMatch.skill} (E:${skillMatch.engineerYears || 0}年/要:${skillMatch.projectRequiredYears}年)`}
                              size="small"
                              color={skillMatch.match ? 'success' : 'error'}
                              variant={skillMatch.required ? 'filled' : 'outlined'}
                            />
                          ))}
                        </Box>

                        <Typography variant="body2" color="textSecondary">
                          経験年数: {match.engineer.experience_years || 0}年
                        </Typography>
                      </CardContent>
                    </Card>

                    {/* 案件情報 */}
                    <Card 
                      variant="outlined"
                      onClick={() => navigate(`/project/${match.project.id}`)}
                      sx={{
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        '&:hover': {
                          backgroundColor: '#f5f5f5',
                          transform: 'translateY(-2px)',
                          boxShadow: 2,
                        },
                      }}
                    >
                      <CardContent sx={{ py: 0.75 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 0.25 }}>
                          {match.project.title}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                          <Business fontSize="small" color="action" />
                          <Typography variant="body2" color="textSecondary">
                            {match.project.client_name || '未設定'}
                          </Typography>
                        </Box>

                        <Box sx={{ display: 'grid', gap: 0.25, mb: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <AttachMoney fontSize="small" color="action" />
                            <Typography variant="body2">
                              ¥{match.project.budget?.toLocaleString() || '応相談'}/月
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Schedule fontSize="small" color="action" />
                            <Typography variant="body2">
                              {match.project.start_date || '未設定'} - {match.project.end_date || '未設定'}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <LocationOn fontSize="small" color="action" />
                            <Typography variant="body2">
                              {match.project.location || '未設定'} {match.project.remote_work && '(リモート可)'}
                            </Typography>
                          </Box>
                        </Box>

                        <Typography variant="body2" sx={{ mb: 0.25, fontWeight: 'bold' }}>
                          必要スキル
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.25, mb: 1 }}>
                          {match.project.skills.map((skill, index) => (
                            <Chip
                              key={`${skill.skill_name}-${index}`}
                              label={`${skill.skill_name} (${skill.required_years}年)`}
                              size="small"
                              color={skill.is_required ? "primary" : "default"}
                              variant={skill.is_required ? "filled" : "outlined"}
                            />
                          ))}
                        </Box>

                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                          <Chip
                            label={getStatusText(match.status)}
                            color={getStatusColor(match.status) as any}
                            sx={{ fontWeight: 'bold' }}
                          />
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            {match.status === 'pending' && (
                              <Button
                                variant="contained"
                                startIcon={<Send />}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleSendProposal(match);
                                }}
                                sx={{ bgcolor: '#27ae60' }}
                              >
                                提案送信
                              </Button>
                            )}
                            {match.status === 'sent' && (
                              <>
                                <Button
                                  variant="contained"
                                  startIcon={<Check />}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleUpdateStatus(match, 'confirmed');
                                  }}
                                  sx={{ bgcolor: '#27ae60' }}
                                  size="small"
                                >
                                  承諾
                                </Button>
                                <Button
                                  variant="outlined"
                                  startIcon={<Close />}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleUpdateStatus(match, 'cancelled');
                                  }}
                                  color="error"
                                  size="small"
                                >
                                  辞退
                                </Button>
                              </>
                            )}
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  </Box>

                  {/* マッチング詳細情報 */}
                  <Box sx={{ mt: 1, pt: 1, borderTop: 1, borderColor: 'divider' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="caption" color="textSecondary">
                        マッチング作成: {match.createdAt}
                        {match.sentAt && ` | 送信: ${match.sentAt}`}
                        {match.responseAt && ` | 回答: ${match.responseAt}`}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <IconButton 
                          size="small" 
                          title="エンジニア詳細"
                          onClick={() => navigate(`/engineer/${match.engineer.id}`)}
                        >
                          <Person />
                        </IconButton>
                        <IconButton 
                          size="small" 
                          title="案件詳細"
                          onClick={() => navigate(`/project/${match.project.id}`)}
                        >
                          <Work />
                        </IconButton>
                      </Box>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Box>

          {filteredMatches.length === 0 && (
            <Card>
              <CardContent sx={{ textAlign: 'center', py: 6 }}>
                <Typography variant="h6" color="textSecondary">
                  該当するマッチング結果がありません
                </Typography>
              </CardContent>
            </Card>
          )}

          {/* 提案送信ダイアログ */}
          <Dialog open={openSendDialog} onClose={() => setOpenSendDialog(false)} maxWidth="md" fullWidth>
            <DialogTitle>
              案件提案を送信
              {selectedMatch && (
                <Typography variant="subtitle1" color="textSecondary">
                  {selectedMatch.engineer.name}様 → {selectedMatch.project.title}
                </Typography>
              )}
            </DialogTitle>
            <DialogContent>
              <TextField
                fullWidth
                multiline
                rows={12}
                label="提案メッセージ"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                sx={{ mt: 1 }}
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setOpenSendDialog(false)}>キャンセル</Button>
              <Button 
                variant="contained" 
                startIcon={<Send />}
                onClick={handleSendMessage}
              >
                送信
              </Button>
            </DialogActions>
          </Dialog>

          {/* ステータス更新ダイアログ */}
          <Dialog open={openStatusDialog} onClose={() => setOpenStatusDialog(false)} maxWidth="sm" fullWidth>
            <DialogTitle>
              提案ステータス更新
              {selectedMatch && (
                <Typography variant="subtitle1" color="textSecondary">
                  {selectedMatch.engineer.name}様 → {selectedMatch.project.title}
                </Typography>
              )}
            </DialogTitle>
            <DialogContent>
              <Typography variant="body1" sx={{ mb: 2 }}>
                この提案を{proposalAction === 'confirmed' ? '承諾' : '辞退'}しますか？
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={4}
                label={`${proposalAction === 'confirmed' ? '承諾' : '辞退'}理由・メモ（任意）`}
                value={statusNotes}
                onChange={(e) => setStatusNotes(e.target.value)}
                sx={{ mt: 1 }}
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setOpenStatusDialog(false)}>キャンセル</Button>
              <Button 
                variant="contained" 
                onClick={handleConfirmStatusUpdate}
                color={proposalAction === 'confirmed' ? 'success' : 'error'}
              >
                {proposalAction === 'confirmed' ? '承諾' : '辞退'}
              </Button>
            </DialogActions>
          </Dialog>
        </>
      )}
    </Box>
  );
};

export default Matching;